import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Dropdown de selección múltiple con checkboxes y buscador.
 *
 * @param {object}   props
 * @param {string}   props.label    Texto cuando no hay nada seleccionado (ej. "Todos los hoteles").
 * @param {string[]} props.options  Opciones disponibles.
 * @param {string[]} props.value    Opciones seleccionadas (array controlado).
 * @param {(next: string[]) => void} props.onChange  Callback con el nuevo array.
 * @param {number}   [props.umbralBusqueda=8]  A partir de cuántas opciones aparece
 *   el buscador. En un filtro de tres opciones un buscador es ruido; en el de
 *   agentes, que trae ~200, la lista sin buscador es scroll a ojo.
 * @param {string}   [props.resumen]  Texto del botón cuando el número de
 *   marcados no lo dice bien («Todos menos Tanya» en vez de «Todos: 21»).
 * @param {string}   [props.titulo]   Tooltip del botón en ese mismo caso.
 * @param {string}   [props.textoLimpiar='Limpiar']  Rótulo del botón que
 *   desmarca todo.
 * @param {string}   [props.encabezado]  Una línea fija arriba de la lista que
 *   dice QUÉ significa marcar. Hace falta cuando dos filtros ofrecen las mismas
 *   opciones con sentido contrario («Conducta presente» / «no presente»): con
 *   la lista abierta, el botón que la abrió ya no se ve.
 * @param {Object<string,string>} [props.deshabilitadas]  Opciones que no se
 *   pueden marcar, con el porqué (se ve al pasar el mouse). Una opción ya
 *   marcada nunca se deshabilita: tiene que poder desmarcarse.
 * @param {string}   [props.prefijo='']  Se antepone a la opción en el botón
 *   cuando hay UNA marcada (p. ej. «✓ » / «✗ »).
 */

// Clave de comparación para buscar: minúsculas y SIN acentos.
//
// Lo segundo no es un detalle: la mitad de este catálogo lleva tilde
// ("Información General", "Cotización", "Cancelación") y nadie las teclea al
// buscar. Sin quitar acentos, escribir "informacion" no encuentra nada y el
// buscador parece roto justo en las opciones que más se buscan.
const clave = (s) => String(s)
  .normalize('NFD')
  // Rango escapado a propósito: los combinantes son invisibles en el
  // editor y cualquier copiar/pegar los pierde sin que nada falle.
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

export default function MultiSelectFilter({
  label,
  options = [],
  value = [],
  onChange,
  umbralBusqueda = 8,
  resumen,
  titulo,
  textoLimpiar = 'Limpiar',
  encabezado,
  deshabilitadas = {},
  prefijo = '',
}) {
  const [open, setOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Al abrir: foco en el buscador y término en blanco. Conservar la búsqueda
  // anterior haría que el filtro se abriera mostrando media lista, sin que se
  // vea por qué faltan opciones.
  useEffect(() => {
    if (!open) return;
    setBusqueda('');
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  const conBuscador = options.length >= umbralBusqueda;

  const visibles = useMemo(() => {
    const termino = clave(busqueda.trim());
    if (!termino) return options;
    return options.filter((o) => clave(o).includes(termino));
  }, [options, busqueda]);

  const bloqueada = (opt) => !value.includes(opt) && Boolean(deshabilitadas[opt]);

  const toggle = (opt) => {
    if (bloqueada(opt)) return;
    const next = value.includes(opt)
      ? value.filter((v) => v !== opt)
      : [...value, opt];
    onChange(next);
  };

  const summary = resumen || (value.length === 0
    ? label
    : value.length === 1
      ? `${prefijo}${value[0]}`
      : `${label.split(' ')[0]}: ${value.length}`);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="filter-select"
        onClick={() => setOpen((o) => !o)}
        title={titulo || (value.length ? value.join(', ') : label)}
        style={{ cursor: 'pointer', textAlign: 'left', minWidth: 140 }}
      >
        {summary} ▾
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 50,
            marginTop: 4,
            maxHeight: 280,
            overflowY: 'auto',
            minWidth: 220,
            background: '#1a1a22',
            border: '1px solid #374151',
            borderRadius: 6,
            padding: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}
        >
          {/* El buscador y "Limpiar" van pegados arriba: son lo único que sigue
              siendo alcanzable cuando la lista tiene 200 filas y hay scroll.
              "Limpiar" tiene que verse MIENTRAS se busca — si desapareciera,
              quien filtró perdería de vista lo que ya eligió y no tendría cómo
              deseleccionarlo sin borrar el término primero. */}
          {(encabezado || conBuscador || value.length > 0) && (
            <div style={{
              position: 'sticky', top: -6, zIndex: 1,
              background: '#1a1a22', paddingBottom: 4, marginTop: -6, paddingTop: 6,
            }}>
              {encabezado && (
                <div style={{
                  fontSize: 12, fontWeight: 600, color: '#c7d2fe',
                  padding: '2px 6px 6px', borderBottom: '1px solid #2a2a38', marginBottom: 4,
                }}>
                  {encabezado}
                </div>
              )}
              {conBuscador && (
                <input
                  ref={inputRef}
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
                  placeholder="Buscar..."
                  aria-label={`Buscar en ${label}`}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 6, color: '#e0e0e6',
                    padding: '6px 8px', fontSize: 12, fontFamily: 'inherit',
                    outline: 'none',
                  }}
                />
              )}
              {value.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  style={{
                    width: '100%', textAlign: 'left', background: 'transparent',
                    border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer',
                    padding: '4px 6px',
                  }}
                >
                  ✕ {textoLimpiar} ({value.length})
                </button>
              )}
            </div>
          )}
          {options.length === 0 && (
            <div style={{ color: '#6b7280', fontSize: 12, padding: '6px' }}>Sin opciones</div>
          )}
          {/* "Sin coincidencias" y "Sin opciones" son cosas distintas: la
              primera se arregla borrando el término, la segunda no. */}
          {options.length > 0 && visibles.length === 0 && (
            <div style={{ color: '#6b7280', fontSize: 12, padding: '6px' }}>
              Sin coincidencias
            </div>
          )}
          {visibles.map((opt) => (
            <label
              key={opt}
              title={bloqueada(opt) ? deshabilitadas[opt] : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px',
                fontSize: 13, borderRadius: 4,
                color: bloqueada(opt) ? '#4b5563' : '#e5e7eb',
                cursor: bloqueada(opt) ? 'not-allowed' : 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={value.includes(opt)}
                disabled={bloqueada(opt)}
                onChange={() => toggle(opt)}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
